import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Select, Input, Space, Typography, Breadcrumb, Tabs, Modal, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import tenantService from '../services/tenants';
import noticeService from '../services/notices';
import type { Tenant } from '../services/tenants';
import type { NoticeItem } from '../types/notice';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;

type TabType = 'tenant' | 'notice';
type ApprovalStatusType = 'pending' | 'approved' | 'rejected' | 'all';

interface ApprovalDataItem {
  id: number;
  no: number;
  category: string;
  exposure: string;
  company: string;
  title: string;
  requester: string;
  requestDate: string;
  status: string;
  createdAt: string;
  originalData: Tenant | NoticeItem;
}

interface FilterOptions {
  category: string;
  exposureType: string;
  status: ApprovalStatusType;
  searchKeyword: string;
  page: number;
  pageSize: number;
  tab: TabType;
}

// 승인 상태 한글 변환
const getApprovalStatusText = (status: string): string => {
  switch (status) {
    case 'pending':
      return '승인대기';
    case 'approved':
      return '승인완료';
    case 'rejected':
      return '승인거부';
    default:
      return status;
  }
};

// 노출 상태 한글 변환
const getExposureText = (exposure: string | boolean): string => {
  if (typeof exposure === 'boolean') {
    return exposure ? '노출' : '비노출';
  }
  switch (exposure) {
    case 'show':
    case 'active':
      return '노출';
    case 'hide':
    case 'inactive':
      return '비노출';
    default:
      return exposure;
  }
};

const ContentApprovalManagement: React.FC = () => {
  const navigate = useNavigate();

  // 상태 관리
  const [approvalData, setApprovalData] = useState<ApprovalDataItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('tenant');
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [filters, setFilters] = useState<FilterOptions>({
    category: '전체',
    exposureType: '전체',
    status: 'all',
    searchKeyword: '',
    page: 1,
    pageSize: 10,
    tab: 'tenant',
  });
  const [total, setTotal] = useState<number>(0);

  // 테이블 컬럼 정의 (탭별로 다른 컬럼)
  const getColumns = (): ColumnsType<ApprovalDataItem> => {
    const baseColumns: ColumnsType<ApprovalDataItem> = [
      {
        title: 'No.',
        dataIndex: 'no',
        key: 'no',
        width: 80,
        align: 'center',
        render: (_, __, index) => (filters.page - 1) * filters.pageSize + index + 1,
      },
      {
        title: '분류',
        dataIndex: 'category',
        key: 'category',
        width: 100,
        align: 'center',
        render: (category: string) => (
          <Text style={{ color: '#ffffff' }}>{category}</Text>
        ),
      },
      {
        title: '노출여부',
        dataIndex: 'exposure',
        key: 'exposure',
        width: 100,
        align: 'center',
        render: (exposure: string) => (
          <Text style={{ color: '#ffffff' }}>{exposure}</Text>
        ),
      },
    ];

    // 입주사 소개 탭일 때만 회사명 컬럼 추가
    if (activeTab === 'tenant') {
      baseColumns.push({
        title: '회사명',
        dataIndex: 'company',
        key: 'company',
        width: 250,
        render: (text: string) => (
          <Text style={{ color: '#ffffff' }}>{text}</Text>
        ),
      });
    }

    // 나머지 공통 컬럼
    baseColumns.push(
      {
        title: '제목',
        dataIndex: 'title',
        key: 'title',
        ellipsis: true,
        render: (text: string) => (
          <Text style={{ color: '#ffffff' }}>{text}</Text>
        ),
      },
      {
        title: '요청자',
        dataIndex: 'requester',
        key: 'requester',
        width: 120,
        align: 'center',
        render: (text: string) => (
          <Text style={{ color: '#a6a6a6' }}>{text}</Text>
        ),
      },
      {
        title: '요청일',
        dataIndex: 'requestDate',
        key: 'requestDate',
        width: 120,
        align: 'center',
        render: (date: string) => (
          <Text style={{ color: '#a6a6a6' }}>{date}</Text>
        ),
      },
      {
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        align: 'center',
        render: (status: string) => {
          // 상태별 태그 색상 정의 (공지사항/입주사 소개와 동일)
          if (status === '승인거부') {
            return <Tag color="red">{status}</Tag>;
          } else if (status === '승인완료') {
            return <Tag color="blue">{status}</Tag>;
          } else if (status === '승인대기') {
            return <Tag color="default">{status}</Tag>;
          }
          return <Tag color="default">{status}</Tag>;
        },
      }
    );

    return baseColumns;
  };

  // 입주사 데이터 변환
  const transformTenantData = (tenants: Tenant[]): ApprovalDataItem[] => {
    return tenants.map((tenant) => ({
      id: tenant.id,
      no: tenant.id,
      category: tenant.createdAt && new Date(tenant.createdAt).getTime() > Date.now() - 86400000 ? '신규' : '수정',
      exposure: getExposureText(tenant.status || 'inactive'),
      company: tenant.name,
      title: tenant.description || '-',
      requester: tenant.creator?.name || tenant.creator?.email || 'Admin',
      requestDate: tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').slice(0, -1) : '-',
      status: getApprovalStatusText(tenant.approvalStatus),
      createdAt: tenant.createdAt || '',
      originalData: tenant
    }));
  };

  // 공지사항 데이터 변환
  const transformNoticeData = (notices: NoticeItem[]): ApprovalDataItem[] => {
    return notices.map((notice) => ({
      id: notice.id,
      no: notice.id,
      category: notice.createdAt && new Date(notice.createdAt).getTime() > Date.now() - 86400000 ? '신규' : '수정',
      exposure: getExposureText(notice.exposure || 'hide'),
      company: '-',
      title: notice.title,
      requester: notice.creator?.name || notice.creator?.email || 'Admin',
      requestDate: notice.createdAt ? new Date(notice.createdAt).toLocaleDateString('ko-KR').replace(/\. /g, '.').slice(0, -1) : '-',
      status: getApprovalStatusText(notice.approvalStatus),
      createdAt: notice.createdAt || '',
      originalData: notice
    }));
  };

  // 데이터 로드 함수
  const loadApprovalData = useCallback(async () => {
    setLoading(true);
    try {
      if (filters.tab === 'tenant') {
        // 입주사 데이터 로드
        const params: any = {
          page: filters.page,
          limit: filters.pageSize,
          search: filters.searchKeyword || undefined,
        };

        // 승인 상태 필터
        if (filters.status && filters.status !== 'all') {
          params.approvalStatus = filters.status;
        }

        // 노출 상태 필터
        if (filters.exposureType && filters.exposureType !== '전체') {
          params.status = filters.exposureType === '노출' ? 'active' : 'inactive';
        }

        const response = await tenantService.getTenants(params);
        let transformedData = transformTenantData(response.data.tenants);

        // 분류 필터 (클라이언트 사이드)
        if (filters.category && filters.category !== '전체') {
          transformedData = transformedData.filter(item => item.category === filters.category);
        }

        setApprovalData(transformedData);
        setTotal(filters.category !== '전체' ? transformedData.length : response.data.pagination.totalItems);
      } else if (filters.tab === 'notice') {
        // 공지사항 데이터 로드
        const params: any = {
          page: filters.page,
          limit: filters.pageSize,
          search: filters.searchKeyword || undefined,
        };

        // 승인 상태 필터
        if (filters.status && filters.status !== 'all') {
          params.approvalStatus = filters.status;
        }

        // 노출 상태 필터
        if (filters.exposureType && filters.exposureType !== '전체') {
          params.exposure = filters.exposureType === '노출' ? 'show' : 'hide';
        }

        const response = await noticeService.getNoticeList(params);
        let transformedData = transformNoticeData(response.data.notices);

        // 분류 필터 (클라이언트 사이드)
        if (filters.category && filters.category !== '전체') {
          transformedData = transformedData.filter(item => item.category === filters.category);
        }

        setApprovalData(transformedData);
        setTotal(filters.category !== '전체' ? transformedData.length : response.data.pagination.total);
      }
    } catch (error) {
      message.error('데이터를 불러오는 중 오류가 발생했습니다.');
      console.error('Load approval data error:', error);
      setApprovalData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // 컴포넌트 마운트 시 및 필터 변경 시 데이터 로드
  useEffect(() => {
    loadApprovalData();
  }, [loadApprovalData]);

  // 탭 변경 핸들러
  const handleTabChange = (key: string) => {
    const tabType = key as TabType;
    setActiveTab(tabType);
    setSelectedRowKeys([]);
    setFilters(prev => ({
      ...prev,
      tab: tabType,
      page: 1,
      searchKeyword: '',
      category: '전체',
      status: 'all',
      exposureType: '전체'
    }));
  };

  // 필터 변경 핸들러
  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  // 검색 핸들러
  const handleSearch = (value: string) => {
    handleFilterChange('searchKeyword', value);
  };

  // 페이지네이션 핸들러
  const handlePageChange = (page: number, pageSize?: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      pageSize: pageSize || prev.pageSize,
    }));
  };



  // 체크박스 선택 핸들러
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // 승인 처리 핸들러
  const handleApproval = async (approvalStatus: 'approved' | 'rejected' | 'pending') => {
    if (selectedRowKeys.length === 0) {
      message.warning('처리할 항목을 선택해주세요.');
      return;
    }

    // 승인 거부 시 모달 표시
    if (approvalStatus === 'rejected') {
      setRejectModalVisible(true);
      return;
    }

    // 승인 또는 승인 대기 처리
    try {
      setLoading(true);
      const statusText =
        approvalStatus === 'approved' ? '승인완료' : '승인대기';

      // 선택된 항목들을 순차적으로 처리
      for (const key of selectedRowKeys) {
        const item = approvalData.find(d => d.id === key);
        if (!item) continue;

        if (filters.tab === 'tenant') {
          await tenantService.approveTenant(item.id, approvalStatus);
        } else if (filters.tab === 'notice') {
          await noticeService.approveNotice(item.id, approvalStatus);
        }
      }

      message.success("승인 되었습니다.");
      setSelectedRowKeys([]);

      // 데이터 새로고침
      await loadApprovalData();
    } catch (error) {
      console.error('Approval error:', error);
      message.error('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 승인 거부 확인 핸들러
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      message.warning('사유를 입력해주세요.');
      return;
    }

    try {
      setLoading(true);

      // 선택된 항목들을 순차적으로 처리
      for (const key of selectedRowKeys) {
        const item = approvalData.find(d => d.id === key);
        if (!item) continue;

        if (filters.tab === 'tenant') {
          await tenantService.approveTenant(item.id, 'rejected', rejectReason);
        } else if (filters.tab === 'notice') {
          await noticeService.approveNotice(item.id, 'rejected', rejectReason);
        }
      }

      message.success(`승인 거부 되었습니다.`);
      setRejectModalVisible(false);
      setRejectReason('');
      setSelectedRowKeys([]);

      // 데이터 새로고침
      await loadApprovalData();
    } catch (error) {
      console.error('Reject error:', error);
      message.error('승인 거부 처리에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 승인 거부 취소 핸들러
  const handleRejectCancel = () => {
    setRejectModalVisible(false);
    setRejectReason('');
  };

  // 행 클릭 핸들러
  const handleRowClick = (record: ApprovalDataItem) => {
    if (filters.tab === 'tenant') {
      navigate(`/content-approval/detail/${record.id}?type=tenant`);
    } else if (filters.tab === 'notice') {
      navigate(`/content-approval/detail/${record.id}?type=notice`);
    }
  };

  // 탭 아이템 (이벤트 탭 제거)
  const tabItems = [
    {
      key: 'tenant',
      label: '입주사 소개',
      children: null,
    },
    {
      key: 'notice',
      label: '공지사항',
      children: null,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '콘텐츠 승인 관리' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        콘텐츠 승인 관리
      </Title>

      {/* 탭 */}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{
          marginBottom: '24px',
        }}
      />

      {/* 필터 영역 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>분류</Text>
            <Select
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
              style={{ width: 120 }}
              styles={{ popup: { root: { background: '#1f1f1f' } } }}
            >
              <Option value="전체">전체</Option>
              <Option value="신규">신규</Option>
              <Option value="수정">수정</Option>
            </Select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>노출여부</Text>
            <Select
              value={filters.exposureType}
              onChange={(value) => handleFilterChange('exposureType', value)}
              style={{ width: 120 }}
              styles={{ popup: { root: { background: '#1f1f1f' } } }}
            >
              <Option value="전체">전체</Option>
              <Option value="노출">노출</Option>
              <Option value="비노출">비노출</Option>
            </Select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text>상태</Text>
            <Select
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: 120 }}
              styles={{ popup: { root: { background: '#1f1f1f' } } }}
            >
              <Option value="all">전체</Option>
              <Option value="pending">승인대기</Option>
              <Option value="approved">승인완료</Option>
              <Option value="rejected">승인거부</Option>
            </Select>
          </div>
        </div>
      </div>

      {/* 테이블 상단 정보 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <Text>총 {total}건</Text>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            onClick={() => handleApproval('pending')}
            disabled={selectedRowKeys.length === 0}
            style={{
              height: 32,
              background: selectedRowKeys.length === 0 ? 'transparent' : '#a6a6a6',
              border: '1px solid #303030',
              color: selectedRowKeys.length === 0 ? '#666666' : '#ffffff',
              padding: '0 16px'
            }}
          >
            승인취소
          </Button>
          <Button
            onClick={() => handleApproval('rejected')}
            disabled={selectedRowKeys.length === 0}
            style={{
              height: 32,
              background: selectedRowKeys.length === 0 ? 'transparent' : '#ff4d4f',
              border: '1px solid #303030',
              color: selectedRowKeys.length === 0 ? '#666666' : '#ffffff',
              padding: '0 16px'
            }}
          >
            승인거부
          </Button>
          <Button
            type="primary"
            onClick={() => handleApproval('approved')}
            disabled={selectedRowKeys.length === 0}
            style={{
              height: 32,
              background: selectedRowKeys.length === 0 ? 'transparent' : '#F24822',
              borderColor: selectedRowKeys.length === 0 ? '#303030' : '#F24822',
              border: selectedRowKeys.length === 0 ? '1px solid #303030' : 'none',
              color: selectedRowKeys.length === 0 ? '#666666' : '#ffffff',
              padding: '0 16px'
            }}
          >
            승인완료
          </Button>
          <Input
            placeholder="검색"
            suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
            value={filters.searchKeyword}
            allowClear
            onChange={(e) => setFilters(prev => ({ ...prev, searchKeyword: e.target.value }))}
            onPressEnter={() => handleSearch(filters.searchKeyword)}
            style={{
              width: 400,
              height: 40,
              borderRadius: 8,
              background: '#2a2a2a',
              border: '1px solid #404040',
              color: '#ffffff'
            }}
          />
        </div>
      </div>

      {/* 테이블 */}
      <Table
        rowSelection={rowSelection}
        columns={getColumns()}
        dataSource={approvalData}
        rowKey="id"
        loading={loading}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: filters.page,
          total: total,
          pageSize: filters.pageSize,
          onChange: handlePageChange,
          showSizeChanger: true,
          showQuickJumper: false,
          pageSizeOptions: ['10', '20', '50'],
          style: { textAlign: 'center' },
        }}
        style={{
          backgroundColor: '#1f1f1f',
        }}
        size="middle"
      />

      {/* 승인 거부 모달 */}
      <Modal
        title="승인 거부"
        open={rejectModalVisible}
        onOk={handleRejectConfirm}
        onCancel={handleRejectCancel}
        okText="확인"
        cancelText="취소"
        centered
        width={600}
        destroyOnHidden={true}
        maskClosable={false}
        okButtonProps={{
          size: 'large',
          style: {
            minWidth: 100,
            height: 48,
            fontSize: '16px',
            fontWeight: 500,
            backgroundColor: '#F24822',
            color: '#ffffff',
            borderColor: '#F24822'
          }
        }}
        cancelButtonProps={{
          size: 'large',
          style: {
            minWidth: 100,
            height: 48,
            fontSize: '16px',
            fontWeight: 500
          }
        }}
        styles={{
          header: {
            paddingBottom: 16,
            marginBottom: 24,
            borderBottom: '1px solid #303030'
          },
          body: {
            paddingTop: 0
          },
          footer: {
            marginTop: 32,
            paddingTop: 24,
            borderTop: '1px solid #303030'
          }
        }}
      >
        <div style={{ marginBottom: 0 }}>
          <div style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>
              사유 <span style={{ color: '#ff4d4f' }}>*</span>
            </span>
          </div>
          <Input.TextArea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="사유를 입력하세요"
            maxLength={30}
            showCount
            rows={4}
            style={{
              resize: 'none',
              fontSize: '14px'
            }}
          />
        </div>
      </Modal>
    </Space>
  );
};

export default ContentApprovalManagement;
